// ════════════════════════════════════════════════════════════
//  StadiumCrowd.jsx — THOUSANDS IN THE STANDS, ONE DRAW CALL
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-16: "back them up maybe 3 times as far back … fill the
//  gaps with more seats and beings so its more like a stadium with a top
//  deck level"
//
//  ~2,900 seats can't each be a full being (each is dozens of meshes —
//  ie-realtime-perf). So, once, when Ringside opens:
//
//   1. THE PHOTO SHOOT — every being in the approved pool, at every tier,
//      is mounted ONE time into an off-screen scene laid out as a grid,
//      lit like the arena, and photographed into one atlas texture
//      (render target, MSAA, mipmapped). Then the off-screen bodies are
//      unmounted — they cost nothing after that.
//   2. THE CROWD — one InstancedMesh of quads. Each instance reads its own
//      cell of the atlas, turns to face the camera around its vertical
//      axis, and bobs / jumps with the cheer. One draw call for everyone.
//
//  The bodies are the vendored kits, untouched: these are photos OF them.
//  Photos lose their idle animation and the faint aura bubbles (alpha below
//  the cut-out is dropped) — at 40–60 units out neither reads anyway.
//  Until the shoot finishes (a few frames) the stands are simply empty.
// ════════════════════════════════════════════════════════════
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { IEBeing, mulberry32 } from "../ieBeingModels.jsx";
import { TerrorSpecies } from "../ieTerrorSpecies.jsx";
import { CROWD_POOL, CROWD_TIERS, CROWD_HEIGHT, CROWD_FILL, SEAT_GAP, STADIUM, ISLAND_BOB, WAVE } from "../data/ringside.js";

// ── THE ISLANDS — every section of every ring, in one shared list ──
// Ringside.jsx builds the rock from this list and the crowd rides it, so the two
// can never disagree about where an island is or how high it's floating.
const LIFT_RNG = mulberry32(4242);
export const ISLANDS = STADIUM.flatMap((d, di) => {
  const span = (Math.PI * 2) / d.sections, gap = THREE.MathUtils.degToRad(d.gapDeg), off = THREE.MathUtils.degToRad(d.phase || 0);
  const [lo, hi] = d.lift || [0, 0];
  return Array.from({ length: d.sections }, (_, s) => ({ deck: d, di, s, phi0: off + s * span + gap / 2, len: span - gap, lift: lo + LIFT_RNG() * (hi - lo) }));
});
// how high island i is floating at time t (seconds): its own lift + a slow swell
// + a slower drift — the ONE formula the rock, the crowd and the tethers all use
export const islandBob = (i, t) => ISLANDS[i].lift +
  Math.sin(t * ISLAND_BOB.speed + i * 1.7) * ISLAND_BOB.amp + Math.sin(t * ISLAND_BOB.speed2 + i * 2.9) * ISLAND_BOB.amp2;

const TIERS = ["beb", "champion", "lord"];
// every (being, tier) is one photo
const CELLS = CROWD_POOL.flatMap((who) => TIERS.map((tier) => ({ ...who, tier })));
const GRID = Math.ceil(Math.sqrt(CELLS.length));     // 147 photos → 13 × 13
const ATLAS_PX = 2048;
const FILL = 0.82;                                     // a being fills this much of its cell (a clear margin round every photo)
const FOOT = 0.06;                                     // feet sit this far up the cell

// ── one photo subject: fitted to its cell by height AND width ──
function Subject({ kit, id, tier, col, row, holder }) {
  const fit = useRef(), inner = useRef();
  useLayoutEffect(() => {
    if (!fit.current || !inner.current) return;
    fit.current.scale.setScalar(1);
    fit.current.position.set(0, 0, 0);
    inner.current.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(inner.current, true);   // precise: real vertices, not loose bounds
    // ⚠ the box is in WORLD space, which includes this cell's own offset — take
    // it out, or every being is pushed out of its cell (found live 2026-10-16:
    // only column 0 had anyone in it)
    box.translate(fit.current.parent.getWorldPosition(new THREE.Vector3()).negate());
    const h = Math.max(0.2, box.max.y - box.min.y), w = Math.max(0.2, box.max.x - box.min.x);
    const s = Math.min(FILL / h, FILL / w);
    fit.current.scale.setScalar(s);
    // centre it in x, stand its lowest point on the cell's floor
    fit.current.position.set(-((box.min.x + box.max.x) / 2) * s, -box.min.y * s, 0);
  }, [kit, id, tier]);
  return (
    <group ref={holder} position={[col + 0.5, row + FOOT, 0]}>
      <group ref={fit}>
        <group ref={inner}>{kit === "species" ? <TerrorSpecies being={id} tier={tier} /> : <IEBeing being={id} tier={tier} />}</group>
      </group>
    </group>
  );
}

// ── the shoot: mount, wait for every body to settle, photograph, unmount ──
function PhotoShoot({ onDone }) {
  const { gl } = useThree();
  const scene = useMemo(() => new THREE.Scene(), []);
  const cam = useMemo(() => {
    const c = new THREE.OrthographicCamera(0, GRID, GRID, 0, 0.1, 100);
    c.position.set(0, 0, 50); c.lookAt(0, 0, 0);
    // after lookAt, re-assert the frustum: x 0..GRID, y 0..GRID in world units
    c.left = 0; c.right = GRID; c.top = GRID; c.bottom = 0; c.updateProjectionMatrix();
    return c;
  }, []);
  const frames = useRef(0);
  const done = useRef(false);
  const holders = useRef([]);
  useFrame(() => {
    if (done.current) return;
    if (++frames.current < 4) return;   // let every body mount, fit and build its textures
    done.current = true;
    // ⚠ NO MSAA (samples: 0) — a multisampled target is resolved and then
    // INVALIDATED after every render, so with one render per cell only the last
    // photo survived (found live, 2026-10-16: a stadium of one green being).
    const rt = new THREE.WebGLRenderTarget(ATLAS_PX, ATLAS_PX, { samples: 0, generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter });
    const prevClear = gl.getClearColor(new THREE.Color()), prevAlpha = gl.getClearAlpha(), prevAuto = gl.autoClear;
    gl.setClearColor(0x000000, 0);
    gl.setRenderTarget(rt);
    gl.clear(true, true, true);
    // the album is cleared ONCE; each photo then only clears its own depth,
    // so no shot can wipe the ones before it
    gl.autoClear = false;
    // ONE PHOTO AT A TIME: only this subject visible, the camera framed on its
    // cell, the pixels clipped to its square — a wing or crown that reaches past
    // its cell is cut at its own edge and can never land in a neighbour's photo.
    const px = ATLAS_PX / GRID, hs = holders.current;
    hs.forEach((h) => { if (h) h.visible = false; });
    CELLS.forEach((_, i) => {
      const h = hs[i]; if (!h) return;
      const col = i % GRID, row = Math.floor(i / GRID);
      const x0 = Math.round(col * px), y0 = Math.round(row * px);
      const w = Math.round((col + 1) * px) - x0, hh = Math.round((row + 1) * px) - y0;
      h.visible = true;
      cam.left = col; cam.right = col + 1; cam.bottom = row; cam.top = row + 1; cam.updateProjectionMatrix();
      rt.viewport.set(x0, y0, w, hh); rt.scissor.set(x0, y0, w, hh); rt.scissorTest = true;
      gl.setRenderTarget(rt);   // re-apply: the renderer reads the target's viewport/scissor here
      gl.clearDepth();
      gl.render(scene, cam);
      h.visible = false;
    });
    rt.scissorTest = false; rt.viewport.set(0, 0, ATLAS_PX, ATLAS_PX); rt.scissor.set(0, 0, ATLAS_PX, ATLAS_PX);
    gl.setRenderTarget(null);
    gl.setClearColor(prevClear, prevAlpha);
    gl.autoClear = prevAuto;
    if (typeof window !== "undefined") window.__ringsideAtlas = { rt, gl, grid: GRID, cells: CELLS.length };   // console handle for checking the photos
    onDone(rt);
  });
  return createPortal(
    <>
      {/* lit like the arena (Arena.jsx Lights), but from the front: the crowd faces the ring */}
      <hemisphereLight args={["#b9a6ff", "#1a0f22", 0.85]} />
      <ambientLight intensity={0.3} />
      <directionalLight position={[3, 6, 10]} intensity={2.0} color="#fff0dc" />
      <directionalLight position={[-6, 4, -8]} intensity={1.1} color="#7ce7ff" />
      {CELLS.map((c, i) => <Subject key={i} {...c} col={i % GRID} row={Math.floor(i / GRID)} holder={(el) => (holders.current[i] = el)} />)}
    </>,
    scene,
  );
}

// ── the seats: every tread of every section of both decks ──
export function seatPlan(seed = 16) {
  const R = mulberry32(seed);
  const tierCells = TIERS.map((_, t) => CELLS.map((c, i) => (c.tier === TIERS[t] ? i : -1)).filter((i) => i >= 0));
  const seats = [];
  ISLANDS.forEach((isle, ii) => {
    const d = isle.deck, a0 = isle.phi0, len = isle.len;
    for (let row = 0; row < d.rows; row++) {
      const r = d.r0 + (row + 0.5) * d.dr, y = d.y0 + row * d.dy;
      const n = Math.max(1, Math.floor((len * r) / SEAT_GAP));
      for (let k = 0; k < n; k++) {
        if (R() > CROWD_FILL) continue;
        const roll = R();
        const t = roll < CROWD_TIERS[0] ? 0 : roll < CROWD_TIERS[0] + CROWD_TIERS[1] ? 1 : 2;
        const list = tierCells[t], cell = list[Math.floor(R() * list.length)];
        const a = a0 + ((k + 0.5) / n) * len + (R() - 0.5) * 0.01;
        const rr = r + (R() - 0.5) * 0.3;
        seats.push({ x: Math.sin(a) * rr, y, z: Math.cos(a) * rr, cell, size: CROWD_HEIGHT[TIERS[t]] / FILL, ph: R() * Math.PI * 2, isle: ii });
      }
    }
  });
  return seats;
}

const VERT = /* glsl */`
  attribute vec2 aCell;   // atlas column, row
  attribute vec3 aSeat;   // x = phase, y = height of the quad, z = which island
  uniform float uTime, uCheer, uCorner, uGrid, uWaveHead, uWaveAmp;
  uniform vec4 uHide;   // xy = the camera's seat (x, z), z = hide radius, w = on
  uniform float uIsle[${ISLANDS.length}];   // how high each island is floating right now
  varying vec2 vUv;
  void main() {
    vec3 c = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    c.y += uIsle[int(aSeat.z + 0.5)];       // ride the island
    // the FRONT ROW: the fan whose seat the camera is sitting in steps out of the shot (frontRow.js)
    if (uHide.w > 0.5 && distance(c.xz, uHide.xy) < uHide.z) { vUv = vec2(0.0); gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    // a called corner's half of the stands goes louder (A sits at x < 0)
    float mine = uCorner == 0.0 ? 1.0 : (sign(c.x) == uCorner ? 1.0 : 0.45);
    float ph = aSeat.x;
    float hop = max(0.0, sin(uTime * (5.0 + fract(ph * 1.7) * 2.0) + ph));
    c.y += sin(uTime * 1.4 + ph) * 0.03 + hop * hop * 0.55 * uCheer * mine;
    // THE WAVE: a swell racing round the ring by angle (CrowdProps' signs read the same head/amp)
    float wd = mod(atan(c.x, c.z) - uWaveHead + 3.14159265, 6.2831853) - 3.14159265;
    c.y += exp(-wd * wd * ${WAVE.width.toFixed(1)}) * uWaveAmp * ${WAVE.height.toFixed(2)};
    // stand up straight, turn to face the camera around the vertical axis
    vec3 right = normalize(vec3(viewMatrix[0][0], 0.0, viewMatrix[2][0]));
    vec3 p = c + right * position.x * aSeat.y + vec3(0.0, position.y * aSeat.y, 0.0);
    vUv = (aCell + uv) / uGrid;
    gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
  }
`;
const FRAG = /* glsl */`
  uniform sampler2D uMap;
  varying vec2 vUv;
  void main() {
    vec4 t = texture2D(uMap, vUv);
    if (t.a < 0.45) discard;
    gl_FragColor = vec4(t.rgb / max(t.a, 0.001), 1.0);   // the photo's edges were blended with clear black
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

function Seats({ atlas, cue }) {
  const seats = useMemo(() => seatPlan(), []);
  const mesh = useRef();
  const heat = useRef(0);
  const wave = useRef({ seen: 0, t0: null, a0: 0, next: null });
  const geo = useMemo(() => {
    const g = new THREE.PlaneGeometry(1, 1);
    g.translate(0, 0.5, 0);   // feet at the origin
    const cell = new Float32Array(seats.length * 2), seat = new Float32Array(seats.length * 3);
    seats.forEach((s, i) => {
      cell[i * 2] = s.cell % GRID; cell[i * 2 + 1] = Math.floor(s.cell / GRID);
      seat[i * 3] = s.ph; seat[i * 3 + 1] = s.size; seat[i * 3 + 2] = s.isle;
    });
    g.setAttribute("aCell", new THREE.InstancedBufferAttribute(cell, 2));
    g.setAttribute("aSeat", new THREE.InstancedBufferAttribute(seat, 3));
    return g;
  }, [seats]);
  const mat = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: VERT, fragmentShader: FRAG,
    uniforms: { uMap: { value: atlas.texture }, uTime: { value: 0 }, uCheer: { value: 0 }, uCorner: { value: 0 }, uGrid: { value: GRID }, uWaveHead: { value: 0 }, uWaveAmp: { value: 0 }, uHide: { value: new THREE.Vector4(0, 0, 0.7, 0) },
      uIsle: { value: new Float32Array(ISLANDS.length) } },
  }), [atlas]);
  useLayoutEffect(() => {
    const m = mesh.current; if (!m) return;
    const o = new THREE.Object3D();
    seats.forEach((s, i) => { o.position.set(s.x, s.y, s.z); o.updateMatrix(); m.setMatrixAt(i, o.matrix); });
    m.instanceMatrix.needsUpdate = true;
  }, [seats]);
  useEffect(() => () => { geo.dispose(); mat.dispose(); }, [geo, mat]);
  // tells CrowdProps (signs, balls) the stands are populated — they wait for this
  useEffect(() => { const c = cue.current; c.crowdReady = true; return () => { c.crowdReady = false; }; }, [cue]);
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05), c = cue.current;
    heat.current += ((c.cheer || 0) - heat.current) * Math.min(1, dt * 3);
    mat.uniforms.uTime.value = clock.elapsedTime;
    const isle = mat.uniforms.uIsle.value;
    for (let i = 0; i < isle.length; i++) isle[i] = islandBob(i, clock.elapsedTime);
    // the wave: starts on cue.waveReq (the seam) or by itself every WAVE.every seconds; one lap, eased in and out
    const w = wave.current, t = clock.elapsedTime, rnd = () => WAVE.every[0] + Math.random() * (WAVE.every[1] - WAVE.every[0]);
    if (w.next == null) w.next = t + rnd();
    const req = c.waveReq || 0;
    if (req !== w.seen || t > w.next) { w.seen = req; w.t0 = t; w.a0 = (Math.random() * 2 - 1) * Math.PI; w.next = t + rnd(); }
    const e = w.t0 == null ? -1 : t - w.t0, lap = (Math.PI * 2) / WAVE.speed;
    c.waveAmp = e < 0 ? 0 : Math.max(0, Math.min(1, e / 0.8, (lap + 0.6 - e) / 0.6));
    c.waveHead = w.a0 + WAVE.speed * Math.max(0, e);
    mat.uniforms.uWaveHead.value = c.waveHead; mat.uniforms.uWaveAmp.value = c.waveAmp;
    mat.uniforms.uCheer.value = heat.current;
    mat.uniforms.uHide.value.set(c.hideX || 0, c.hideZ || 0, 0.7, c.hideOn ? 1 : 0);   // 0.7: your own fan (≤0.35 off its seat) goes, the next one (≥0.74 away) stays
    mat.uniforms.uCorner.value = c.corner === "A" ? -1 : c.corner === "B" ? 1 : 0;
  });
  return <instancedMesh ref={mesh} args={[geo, mat, seats.length]} frustumCulled={false} />;
}

export default function StadiumCrowd({ cue }) {
  const [atlas, setAtlas] = useState(null);
  useEffect(() => () => { if (atlas) atlas.dispose(); }, [atlas]);
  return atlas ? <Seats atlas={atlas} cue={cue} /> : <PhotoShoot onDone={setAtlas} />;
}

export const CROWD_COUNT = () => seatPlan().length;   // for the PRIME / a console check
